import { useState, useCallback, useRef } from 'react';
import { JustRightHeightModal, TimelineWrapper } from 'components/styled/scoped';
import { css, cx } from '@emotion/css';
import { ReactComponent as SvgDistribute } from 'views/Conversation/assets/distribute.svg';
import { Button, Tooltip, Timeline, Spin } from 'antd';
import Icon, { InfoCircleOutlined } from '@ant-design/icons';
import { conversationClient } from 'services/api';
import { useRequest } from 'ahooks';
import { match, select } from 'ts-pattern';
import dayjs from 'lib/dayjs';
import TransComponent from 'components/TransComponent';
import { T, useTranslate } from '@tolgee/react';

type AssignContext = Conversation.SysAssignContextContent;

const getDistributeDetail = async (id: string) => {
  const { data } = await conversationClient
    .get<{
      data: {
        assignContext: AssignContext[];
      };
    }>(`/getAssignContexts`, {
      params: {
        conversation_id: id,
      },
    })
    .then((res) => {
      return res.data;
    });
  return data;
};

type DistributeDetailProps = {
  visible: boolean;
  onClose: () => void;
  id: string;
};

const DistributeDetail = (props: DistributeDetailProps) => {
  const { visible, onClose, id } = props;
  const { t } = useTranslate();
  const { data, loading } = useRequest(() => getDistributeDetail(id), {
    refreshDeps: [id],
  });
  const ruleTitleRef = useRef({
    MIN_UNPROCESSED: t('rule_min_unprocessed_title'),
    LAST_ASSISTANT: t('rule_last_assistant_title'),
    MIN_TOTAL_RECEPTION: t('rule_min_total_reception_title'),
    PROCESSED_RANDOM_ASSIGN: t('rule_processed_random_assign_title'),
  });
  const list = data?.assignContext || [];
  const mapContextToText = useCallback(
    (context: AssignContext) => {
      return (
        match(context)
          //买家发起咨询，会话根据[当前待处理买家最少优先规则]分配给[部门]-[角色]-[子账号昵称]
          .with(
            {
              assignSource: 'BUYER_QUERY',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'MIN_UNPROCESSED',
                departmentName: select('department'),
                assistantName: select('nickname'),
                roleName: select('role'),
              },
            },
            (i) => {
              return (
                <T
                  keyName="buyer_query_min_unprocessed"
                  params={{
                    i: <TransComponent content={<Rule rule="MIN_UNPROCESSED" />} />,
                    ...i,
                    rule: ruleTitleRef.current['MIN_UNPROCESSED'],
                  }}
                />
              );
            },
          )
          //买家发起咨询，会话根据[最后服务客服优先规则]分配给[子账号昵称]，[子账号昵称]最近一次回复时间为 [时间]
          .with(
            {
              assignSource: 'BUYER_QUERY',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'LAST_ASSISTANT',
                assistantName: select('nickname'),
                lastAssistantReplyTime: select('time'),
              },
            },
            (i) => {
              const { time, ...rest } = i;
              return (
                <T
                  keyName="buyer_query_last_assistant"
                  params={{
                    i: <TransComponent content={<Rule rule="LAST_ASSISTANT" />} />,
                    ...rest,
                    time: dayjs(Number(time)).format('YYYY-MM-DD HH:mm:ss'),
                    rule: ruleTitleRef.current['LAST_ASSISTANT'],
                  }}
                />
              );
            },
          )
          // 买家发起咨询，会话根据[今日接待买家最少优先规则]分配给[部门]-[角色]-[子账号昵称]
          .with(
            {
              assignSource: 'BUYER_QUERY',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'MIN_TOTAL_RECEPTION',
                departmentName: select('department'),
                assistantName: select('nickname'),
                roleName: select('role'),
              },
            },
            (i) => {
              return (
                <T
                  keyName="buyer_query_min_total_reception"
                  params={{
                    i: <TransComponent content={<Rule rule="MIN_TOTAL_RECEPTION" />} />,
                    ...i,
                    rule: ruleTitleRef.current['MIN_TOTAL_RECEPTION'],
                  }}
                />
              );
            },
          )
          //[子账号昵称]离线时，尚未回复买家，会话根据[当前待处理买家最少优先规则]分配给[部门]-[角色]-[子账号昵称]
          .with(
            {
              assignSource: 'ASSISTANT_OFFLINE',
              assignDest: 'TO_ASSISTANT',
              assistantOfflineSource: {
                assistantName: select('from'),
              },
              toAssistantDest: {
                toAssistantRule: 'MIN_UNPROCESSED',
                departmentName: select('department'),
                assistantName: select('nickname'),
                roleName: select('role'),
              },
            },
            (i) => {
              return (
                <T
                  keyName="assistant_offline_min_unprocessed"
                  params={{
                    i: <TransComponent content={<Rule rule="MIN_UNPROCESSED" />} />,
                    ...i,
                    rule: ruleTitleRef.current['MIN_UNPROCESSED'],
                  }}
                />
              );
            },
          )
          // [子账号昵称]离线时，尚未回复买家，会话根据[最后服务客服优先规则]分配给[子账号昵称]，[子账号昵称]最近一次回复时间为 [时间]
          .with(
            {
              assignSource: 'ASSISTANT_OFFLINE',
              assignDest: 'TO_ASSISTANT',
              assistantOfflineSource: {
                assistantName: select('from'),
              },
              toAssistantDest: {
                toAssistantRule: 'LAST_ASSISTANT',
                assistantName: select('nickname'),
                lastAssistantReplyTime: select('time'),
              },
            },
            (i) => {
              return (
                <T
                  keyName={'assistant_offline_last_assistant'}
                  params={{
                    i: <TransComponent content={<Rule rule="LAST_ASSISTANT" />} />,
                    ...i,
                    time: dayjs(Number(i.time)).format('YYYY-MM-DD HH:mm:ss'),
                    rule: ruleTitleRef.current['LAST_ASSISTANT'],
                  }}
                />
              );
            },
          )
          // [子账号昵称]离线时，尚未回复买家，会话根据[今日接待买家最少优先规则]分配给[部门]-[角色]-[子账号昵称]
          .with(
            {
              assignSource: 'ASSISTANT_OFFLINE',
              assignDest: 'TO_ASSISTANT',
              assistantOfflineSource: {
                assistantName: select('from'),
              },
              toAssistantDest: {
                toAssistantRule: 'MIN_TOTAL_RECEPTION',
                departmentName: select('department'),
                assistantName: select('nickname'),
                roleName: select('role'),
              },
            },
            (i) => {
              return (
                <T
                  keyName="assistant_offline_min_total_reception"
                  params={{
                    i: <TransComponent content={<Rule rule="MIN_TOTAL_RECEPTION" />} />,
                    ...i,
                    rule: ruleTitleRef.current['MIN_TOTAL_RECEPTION'],
                  }}
                />
              );
            },
          )
          // 同步历史待处理会话，会话根据[当前待处理买家最少优先规则]分配给[部门]-[角色]-[子账号昵称]
          .with(
            {
              assignSource: 'SYNC_CONVERSATION',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'MIN_UNPROCESSED',
                departmentName: select('department'),
                assistantName: select('nickname'),
                roleName: select('role'),
              },
            },
            (i) => {
              return (
                <T
                  keyName="sync_conversation_min_unprocessed"
                  params={{
                    i: <TransComponent content={<Rule rule="MIN_UNPROCESSED" />} />,
                    ...i,
                    rule: ruleTitleRef.current['MIN_UNPROCESSED'],
                  }}
                />
              );
            },
          )
          // 同步历史待处理会话，会话根据[最后服务客服优先规则]分配给[子账号昵称]，[子账号昵称]最近一次回复时间为 [时间]
          .with(
            {
              assignSource: 'SYNC_CONVERSATION',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'LAST_ASSISTANT',
                assistantName: select('nickname'),
                lastAssistantReplyTime: select('time'),
              },
            },
            (i) => {
              const { time, ...rest } = i;
              return (
                <T
                  keyName="sync_conversation_last_assistant"
                  params={{
                    i: <TransComponent content={<Rule rule="LAST_ASSISTANT" />} />,
                    ...rest,
                    time: dayjs(Number(time)).format('YYYY-MM-DD HH:mm:ss'),
                    rule: ruleTitleRef.current['LAST_ASSISTANT'],
                  }}
                />
              );
            },
          )
          // 同步历史待处理会话，会话根据[今日接待买家最少优先规则]分配给[部门]-[角色]-[子账号昵称]
          .with(
            {
              assignSource: 'SYNC_CONVERSATION',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'MIN_TOTAL_RECEPTION',
                departmentName: select('department'),
                assistantName: select('nickname'),
                roleName: select('role'),
              },
            },
            (i) => {
              return (
                <T
                  keyName="sync_conversation_min_total_reception"
                  params={{
                    i: <TransComponent content={<Rule rule="MIN_TOTAL_RECEPTION" />} />,
                    ...i,
                    rule: ruleTitleRef.current['MIN_TOTAL_RECEPTION'],
                  }}
                />
              );
            },
          )
          // 黑名单买家今日已自动回复3次，为避免遗漏重要消息，会话根据[当前待处理买家最少优先规则]分配给[部门]-[角色]-[子账号昵称]
          .with(
            {
              assignSource: 'BLOCKED_BUYER_QUERY',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'MIN_UNPROCESSED',
                departmentName: select('department'),
                assistantName: select('nickname'),
                roleName: select('role'),
              },
            },
            (i) => {
              return (
                <T
                  keyName="blocked_buyer_query_min_unprocessed"
                  params={{
                    i: <TransComponent content={<Rule rule="MIN_UNPROCESSED" />} />,
                    ...i,
                    rule: ruleTitleRef.current['MIN_UNPROCESSED'],
                  }}
                />
              );
            },
          )
          // 黑名单买家今日已自动回复3次，为避免遗漏重要消息，会话根据[最后服务客服优先规则]分配给[子账号昵称]，[子账号昵称]最近一次回复时间为 [时间]
          .with(
            {
              assignSource: 'BLOCKED_BUYER_QUERY',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'LAST_ASSISTANT',
                assistantName: select('nickname'),
                lastAssistantReplyTime: select('time'),
              },
            },
            (i) => {
              const { time, ...rest } = i;
              return (
                <T
                  keyName="blocked_buyer_query_last_assistant"
                  params={{
                    i: <TransComponent content={<Rule rule="LAST_ASSISTANT" />} />,
                    ...rest,
                    rule: ruleTitleRef.current['LAST_ASSISTANT'],
                    time: dayjs(Number(time)).format('YYYY-MM-DD HH:mm:ss'),
                  }}
                />
              );
            },
          )
          // 黑名单买家今日已自动回复3次，为避免遗漏重要消息，会话根据[今日接待买家最少优先规则]分配给[部门]-[角色]-[子账号昵称]
          .with(
            {
              assignSource: 'BLOCKED_BUYER_QUERY',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'MIN_TOTAL_RECEPTION',
                departmentName: select('department'),
                assistantName: select('nickname'),
                roleName: select('role'),
              },
            },
            (i) => {
              return (
                <T
                  keyName="blocked_buyer_query_min_total_reception"
                  params={{
                    i: <TransComponent content={<Rule rule="MIN_TOTAL_RECEPTION" />} />,
                    ...i,
                    rule: ruleTitleRef.current['MIN_TOTAL_RECEPTION'],
                  }}
                />
              );
            },
          )
          // [子账号昵称]通过搜索订单号主动联系买家，会话分配给[子账号昵称]
          .with(
            {
              assignSource: 'CONTACT_BUYER',
              assignDest: 'TO_ASSISTANT',
              contactBuyerSource: {
                action: 'SEARCH_ORDER',
                assistantName: select('nickname'),
              },
            },
            (i) => {
              return t('contact_buyer_search_order', i);
            },
          )
          .with(
            {
              assignSource: 'FORCE_CONTACT_BUYER',
              forceContactBuyerSource: {
                action: 'SEARCH_ORDER',
                assistantName: select('nickname'),
              },
            },
            (i) => t('contact_buyer_search_order', i),
          )
          // [子账号昵称]通过订单管理主动联系买家，会话分配给[子账号昵称]
          .with(
            {
              assignSource: 'CONTACT_BUYER',
              assignDest: 'TO_ASSISTANT',
              contactBuyerSource: {
                action: 'ORDER_MANAGEMENT',
                assistantName: select('nickname'),
              },
            },
            (i) => {
              return t('contact_buyer_order_management', i);
            },
          )
          .with(
            {
              assignSource: 'FORCE_CONTACT_BUYER',
              forceContactBuyerSource: {
                action: 'ORDER_MANAGEMENT',
                assistantName: select('nickname'),
              },
            },
            (i) => t('contact_buyer_order_management', i),
          )
          // [子账号昵称]通过订单评价主动联系买家，会话分配给[子账号昵称]
          .with(
            {
              assignSource: 'CONTACT_BUYER',
              assignDest: 'TO_ASSISTANT',
              contactBuyerSource: {
                action: 'RATING',
                assistantName: select('nickname'),
              },
            },
            (i) => {
              return t('contact_buyer_rating', i);
            },
          )
          .with(
            {
              assignSource: 'FORCE_CONTACT_BUYER',
              forceContactBuyerSource: {
                action: 'RATING',
                assistantName: select('nickname'),
              },
            },
            (i) => t('contact_buyer_rating', i),
          )
          // [子账号昵称]通过待办主动联系买家，会话分配给[子账号昵称]
          .with(
            {
              assignSource: 'CONTACT_BUYER',
              assignDest: 'TO_ASSISTANT',
              contactBuyerSource: {
                action: 'TODO',
                assistantName: select('nickname'),
              },
            },
            (i) => {
              return t('contact_buyer_todo', i);
            },
          )
          .with(
            {
              assignSource: 'FORCE_CONTACT_BUYER',
              forceContactBuyerSource: {
                action: 'TODO',
                assistantName: select('nickname'),
              },
            },
            (i) => t('contact_buyer_todo', i),
          )
          // [子账号昵称]主动联系买家，会话分配给[子账号昵称]
          .with(
            {
              assignSource: 'CONTACT_BUYER',
              assignDest: 'TO_ASSISTANT',
              contactBuyerSource: {
                action: 'UNKNOWN_ACTION',
                assistantName: select('nickname'),
              },
            },
            (i) => {
              return t('contact_buyer', i);
            },
          )
          .with(
            {
              assignSource: 'FORCE_CONTACT_BUYER',
              forceContactBuyerSource: {
                action: 'UNKNOWN_ACTION',
                assistantName: select('nickname'),
              },
            },
            (i) => t('contact_buyer', i),
          )
          // [子账号昵称]将会话转发给[子账号昵称]，会话分配给[子账号昵称]
          .with(
            {
              assignSource: 'TRANSFER',
              assignDest: 'TO_ASSISTANT',
              transferSource: {
                operatorName: select('from'),
              },
              toAssistantDest: {
                assistantName: select('nickname'),
              },
            },
            (i) => {
              return t('transfer', i);
            },
          )
          // 买家发起咨询，由于所有在线的可分配客服处于繁忙状态，会话进入待分配队列
          .with(
            {
              assignSource: 'BUYER_QUERY',
              assignDest: 'ADD_QUEUE',
              addQueueDest: {
                addQueueReason: 'ALL_BUSY',
              },
            },
            () => {
              return t('buyer_query_all_busy');
            },
          )
          // 买家发起咨询，由于无可分配客服在线，会话进入待分配队列
          .with(
            {
              assignSource: 'BUYER_QUERY',
              assignDest: 'ADD_QUEUE',
              addQueueDest: {
                addQueueReason: 'ALL_NOT_ONLINE',
              },
            },
            () => {
              return t('buyer_query_all_not_online');
            },
          )
          // 买家发起咨询，由于店铺未分配给客服接待，会话进入待分配队列，建议尽快前往部门管理页面将店铺分配给客服账号
          .with(
            {
              assignSource: 'BUYER_QUERY',
              assignDest: 'ADD_QUEUE',
              addQueueDest: {
                addQueueReason: 'NO_AVAILABLE_ASSISTANT',
              },
            },
            () => {
              return t('buyer_query_no_available_assistant');
            },
          )
          // [子账号昵称]离线时，尚未回复买家，由于所有在线的可分配客服处于繁忙状态，会话进入待分配队列
          .with(
            {
              assignSource: 'ASSISTANT_OFFLINE',
              assignDest: 'ADD_QUEUE',
              assistantOfflineSource: {
                assistantName: select('nickname'),
              },
              addQueueDest: {
                addQueueReason: 'ALL_BUSY',
              },
            },
            (i) => {
              return t('assistant_offline_all_busy', i);
            },
          )
          // [子账号昵称]离线时，尚未回复买家，由于无可分配客服在线，会话进入待分配队列
          .with(
            {
              assignSource: 'ASSISTANT_OFFLINE',
              assignDest: 'ADD_QUEUE',
              assistantOfflineSource: {
                assistantName: select('nickname'),
              },
              addQueueDest: {
                addQueueReason: 'ALL_NOT_ONLINE',
              },
            },
            (i) => {
              return t('assistant_offline_all_not_online', i);
            },
          )
          // [子账号昵称]离线时，尚未回复买家，由于店铺未分配给客服接待，会话进入待分配队列，建议尽快前往部门管理页面将店铺分配给客服账号
          .with(
            {
              assignSource: 'ASSISTANT_OFFLINE',
              assignDest: 'ADD_QUEUE',
              assistantOfflineSource: {
                assistantName: select('nickname'),
              },
              addQueueDest: {
                addQueueReason: 'NO_AVAILABLE_ASSISTANT',
              },
            },
            (i) => {
              return t('assistant_offline_no_available_assistant', i);
            },
          )
          // 同步到历史待处理会话，由于所有在线的可分配客服处于繁忙状态，会话进入待分配队列
          .with(
            {
              assignSource: 'SYNC_CONVERSATION',
              assignDest: 'ADD_QUEUE',
              addQueueDest: {
                addQueueReason: 'ALL_BUSY',
              },
            },
            () => {
              return t('sync_conversation_all_busy');
            },
          )
          // 同步到历史待处理会话，由于无可分配客服在线，会话进入待分配队列
          .with(
            {
              assignSource: 'SYNC_CONVERSATION',
              assignDest: 'ADD_QUEUE',
              addQueueDest: {
                addQueueReason: 'ALL_NOT_ONLINE',
              },
            },
            () => {
              return t('sync_conversation_all_not_online');
            },
          )
          // 同步到历史待处理会话，由于店铺未分配给客服接待，会话进入待分配队列，建议尽快前往部门管理页面将店铺分配给客服账号
          .with(
            {
              assignSource: 'SYNC_CONVERSATION',
              assignDest: 'ADD_QUEUE',
              addQueueDest: {
                addQueueReason: 'NO_AVAILABLE_ASSISTANT',
              },
            },
            () => {
              return t('sync_conversation_no_available_assistant');
            },
          )
          // 黑名单买家今日已自动回复3次，为避免遗漏重要消息，会话进行分配，由于所有在线的可分配客服处于繁忙状态，会话进入待分配队列
          .with(
            {
              assignSource: 'BLOCKED_BUYER_QUERY',
              assignDest: 'ADD_QUEUE',
              addQueueDest: {
                addQueueReason: 'ALL_BUSY',
              },
            },
            () => {
              return t('blocked_buyer_query_all_busy_add_queue');
            },
          )
          // 黑名单买家今日已自动回复3次，为避免遗漏重要消息，会话进行分配，由于无可分配客服在线，会话进入待分配队列
          .with(
            {
              assignSource: 'BLOCKED_BUYER_QUERY',
              assignDest: 'ADD_QUEUE',
              addQueueDest: {
                addQueueReason: 'ALL_NOT_ONLINE',
              },
            },
            () => {
              return t('blocked_buyer_query_all_not_online_add_queue');
            },
          )
          // 黑名单买家今日已自动回复3次，为避免遗漏重要消息，会话进行分配，由于店铺未分配给客服接待，会话进入待分配队列，建议尽快前往部门管理页面将店铺分配给客服账号
          .with(
            {
              assignSource: 'BLOCKED_BUYER_QUERY',
              assignDest: 'ADD_QUEUE',
              addQueueDest: {
                addQueueReason: 'NO_AVAILABLE_ASSISTANT',
              },
            },
            () => {
              return t('blocked_buyer_query_no_available_assistant_add_queue');
            },
          )
          // 会话进入待分配队列
          .with(
            {
              assignDest: 'ADD_QUEUE',
            },
            () => {
              return t('add_queue');
            },
          )
          // 会话处于待分配队列中，根据[当前待处理买家最少优先规则]分配给[部门]-[角色]-[子账号昵称]
          .with(
            {
              assignSource: 'POLL_QUEUE',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'MIN_UNPROCESSED',
                departmentName: select('department'),
                assistantName: select('nickname'),
                roleName: select('role'),
              },
            },
            (i) => {
              return (
                <T
                  keyName="poll_queue_min_unprocessed"
                  params={{
                    i: <TransComponent content={<Rule rule="MIN_UNPROCESSED" />} />,
                    ...i,
                    rule: ruleTitleRef.current['MIN_UNPROCESSED'],
                  }}
                />
              );
            },
          )
          // 会话处于待分配队列中，根据[最后服务客服优先规则]分配给[子账号昵称]，[子账号昵称]最近一次回复时间为 [时间]
          .with(
            {
              assignSource: 'POLL_QUEUE',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'LAST_ASSISTANT',
                assistantName: select('nickname'),
                lastAssistantReplyTime: select('time'),
              },
            },
            (i) => {
              const { time, ...rest } = i;
              return (
                <T
                  keyName="poll_queue_last_assistant"
                  params={{
                    i: <TransComponent content={<Rule rule="LAST_ASSISTANT" />} />,
                    ...rest,
                    time: dayjs(Number(time)).format('YYYY-MM-DD HH:mm:ss'),
                    rule: ruleTitleRef.current['LAST_ASSISTANT'],
                  }}
                />
              );
            },
          )
          // 会话处于待分配队列中，会话根据[今日接待买家最少优先规则]分配给[部门]-[角色]-[子账号昵称]
          .with(
            {
              assignSource: 'POLL_QUEUE',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'MIN_TOTAL_RECEPTION',
                departmentName: select('department'),
                assistantName: select('nickname'),
                roleName: select('role'),
              },
            },
            (i) => {
              return (
                <T
                  keyName="poll_queue_min_total_reception"
                  params={{
                    i: <TransComponent content={<Rule rule="MIN_TOTAL_RECEPTION" />} />,
                    ...i,
                    rule: ruleTitleRef.current['MIN_TOTAL_RECEPTION'],
                  }}
                />
              );
            },
          )
          // 会话处于待分配队列中，由于已被回复，根据[已回复会话直接分配规则]分配给[部门]-[角色]-[子账号昵称]
          .with(
            {
              assignSource: 'POLL_QUEUE',
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                toAssistantRule: 'PROCESSED_RANDOM_ASSIGN',
                departmentName: select('department'),
                assistantName: select('nickname'),
                roleName: select('role'),
              },
            },
            (i) => {
              return (
                <T
                  keyName="poll_queue_processed_random_assign"
                  params={{
                    i: <TransComponent content={<Rule rule="PROCESSED_RANDOM_ASSIGN" />} />,
                    ...i,
                    rule: ruleTitleRef.current['PROCESSED_RANDOM_ASSIGN'],
                  }}
                />
              );
            },
          )
          // 由于会话在待分配队列中，距离最后一条消息时间超过72小时，放弃分配会话
          .with(
            {
              assignSource: 'POLL_QUEUE',
              assignDest: 'ABANDON',
            },
            () => {
              return t('poll_queue_abandon');
            },
          )
          // 由于客服离线时尚未回复，且距离最后一条买家咨询时间超过72小时，放弃分配会话
          .with(
            {
              assignSource: 'ASSISTANT_OFFLINE',
              assignDest: 'ABANDON',
            },
            () => {
              return t('assistant_offline_abandon');
            },
          )
          // 会话分配给[子账号昵称]
          .with(
            {
              assignDest: 'TO_ASSISTANT',
              toAssistantDest: {
                assistantName: select('nickname'),
              },
            },
            (i) => {
              return t('unknown_source', i);
            },
          )
          .otherwise(() => {
            return null;
          })
      );
    },
    [t],
  );

  return (
    <JustRightHeightModal
      title={t('distribute_detail')}
      onCancel={onClose}
      visible={visible}
      centered
      className={css`
        .ant-modal-body {
          padding: 20px 16px !important;
          margin: 0 !important;
          min-height: 300px;
        }
      `}
      width={680}
      footer={null}
    >
      {loading ? (
        <div className="h-[300px] flex items-center">
          <Spin className="!m-auto" />
        </div>
      ) : (
        <TimelineWrapper>
          {list.map((item, index) => {
            return (
              <Timeline.Item key={index}>
                <div className="text-[#232426] text-[14px] leading-[20px]">
                  {mapContextToText(item)}
                </div>
                <div className="mt-[4px] leading-[18px] text-[12px] text-[#84868c]">
                  {dayjs(Number(item.timestamp)).format('YYYY-MM-DD HH:mm:ss')}
                </div>
              </Timeline.Item>
            );
          })}
        </TimelineWrapper>
      )}
    </JustRightHeightModal>
  );
};

function Rule({
  rule,
}: {
  rule: Exclude<AssignContext['toAssistantDest']['toAssistantRule'], 'UNKNOWN_RULE'>;
}) {
  const { t } = useTranslate();
  const ruleRef = useRef({
    MIN_UNPROCESSED: t('rule_min_unprocessed'),
    LAST_ASSISTANT: t('rule_last_assistant'),
    MIN_TOTAL_RECEPTION: t('rule_min_total_reception'),
    PROCESSED_RANDOM_ASSIGN: t('rule_processed_random_assign'),
  });

  return (
    <Tooltip title={ruleRef.current[rule]}>
      <InfoCircleOutlined className="mx-[2px]" />
    </Tooltip>
  );
}

const useDistributeDetail = (id: string) => {
  const [visible, setVisible] = useState(false);

  const open = useCallback(() => {
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
  }, []);

  const instance = visible && <DistributeDetail visible={visible} onClose={close} id={id} />;

  return [open, instance] as const;
};

export const DistributeIcon = ({ id, className }: { id: string; className?: string }) => {
  const { t } = useTranslate();
  const [open, instance] = useDistributeDetail(id);
  return (
    <>
      <Tooltip title={t('distribute_detail')}>
        <Icon
          className={cx('mt-[2px] text-[22px]', className)}
          component={SvgDistribute}
          onClick={() => {
            open();
          }}
        />
      </Tooltip>
      {instance}
    </>
  );
};

export const DistributeButton = ({ id }: { id: string }) => {
  const { t } = useTranslate();
  const [open, instance] = useDistributeDetail(id);
  return (
    <>
      <Button
        type="link"
        onClick={() => {
          open();
        }}
        className="!p-0  !pl-[4px] !m-0 !leading-none !h-[20px] !text-[12px]"
      >
        {t('distribute_detail')}
      </Button>
      {instance}
    </>
  );
};
